import  { useState, useEffect, useMemo, useCallback } from 'react';
import Select from '../ui/Select';
import axiosInstance from '../../api/axiosInstance';
import ProjectConfigModal from './ProjectConfigModal';
import { toast } from 'react-toast';
import ApiKeyModal from './ApiKeyModal';
import {  useAiProviders, useHealedProjects, useProjects, useSelfHeal } from '../../hooks/API/settings.api';
import SelfHeal from './ProjectConfiguration/SelfHeal';
import ProjectSelectDropdown from './ProjectConfiguration/ProjectSelectDropdown';
import { Pagination } from '../ui';
import { useDebounce } from '../../hooks/useDebounce';
import { useLocation } from 'react-router';


const ProjectConfiguration = () => {
  const location = useLocation();
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddButton, setShowAddButton] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedTool, setSelectedTool] = useState(null);
  const [isApiModalOpen, setIsApiModalOpen] = useState(false);
  const [apiKeys, setApiKeys] = useState({});
  const [aiTools, setAiTools] = useState([]);
  const [toolsLoading, setToolsLoading] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [selectedProject, setSelectedProject] = useState('');
  const [aiModels, setAiModels] = useState([]);
  const [modelsLoading, setModelsLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);
  const [enabledProjectIds, setEnabledProjectIds] = useState([]);
  const [selectedApiProvider, setSelectedApiProvider] = useState('');
  const [apiKeyInput, setApiKeyInput] = useState('');
  const [existingAiTool, setExistingAiTool] = useState(null);
  
  // Pagination and search states
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: null });
  
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  
  const { mutateAsync: selfHealTriggerFn, isLoading, isSuccess, isError} = useSelfHeal();
  const {data : healdedProjectData, isLoading:headlerProjectLoading}=useHealedProjects()
 const { data: projects = [] } = useProjects();
  const { data: aiProviders , isLoading: providersLoading } = useAiProviders();


  // console.log('ai provider data -->',aiProviderData);
  // console.log(healdedProjectData.projects)

  const dummyAiTools = [
    {
      id: 1,
      name: 'OpenRouter',
      description: 'Unified API for multiple AI models including Claude, GPT-4, and more',
      provider: 'OpenRouter',
      status: 'connected',
      apiKey: '••••••••••••',
      useCustomUrl: false,
      compressPrompts: true,
      selectedModel: '',
      models: []
    }
  ];

  const fetchProjectConfigs = useCallback(async (page = 1, search = '') => {
    try {
      setLoading(true);
      setError(null);
      setShowAddButton(true);
      
      const queryParams = {
        page: page,
        page_size: pageSize
      };

      // Add workspace_id if available
      if (location.pathname.includes('/dashboard')) {
        const workspaceIdMatch = location.pathname.split('/')[1]?.split('_')[1];
        if (workspaceIdMatch) {
          queryParams.workspace_id = parseInt(workspaceIdMatch, 10);
        }
      }

      // Add search term if provided
      if (search && search.trim() !== '') {
        queryParams.search = search.trim();
      }

      // Add sorting parameters
      if (sortConfig && sortConfig.key) {
        queryParams.sort_type = sortConfig.key === 'project_name' ? 1 : 2;
        queryParams.sort_order = sortConfig.direction === 'asc' ? 1 : 2;
      }

      const response = await axiosInstance.get('/projects/list_project', {
        params: queryParams
      });

      const allConfigs = response.projects || [];
      
      setConfigs(allConfigs);
      setTotalPages(response.total_pages || 1);
      
      if (allConfigs.length === 0 && page === 1) {
        setError('No project configs found');
        setShowAddButton(false);
      }
    } catch (error) {
      console.error('Error fetching project configurations:', error);
      const errorMessage = error?.response?.data?.detail?.message || 'Failed to fetch project configurations';
      setError(errorMessage);

      if (
        errorMessage === 'No projects found for the user' ||
        errorMessage === 'No project configs found'
      ) {
        setShowAddButton(false);
      } else {
        setShowAddButton(true);
      }

      toast.error(error?.response?.data?.detail?.message || 'Failed to load project configurations');
    } finally {
      setLoading(false);
    }
  }, [pageSize, location, sortConfig]);

  // const fetchAiProviders = async () => {
  //   try {
  //     setProvidersLoading(true);
  //     const response = await axiosInstance.get('/ai_tools/ai_provider_dropdown');

  //     let providers = [];
  //     if (response.data && Array.isArray(response.data)) {
  //       providers = response.data;
  //     } else {
  //       providers = response.data?.data || [];
  //     }

  //     setAiProviders(providers);
  //     setSelectedApiProvider('');
  //   } catch (error) {
  //     console.error('Error fetching AI providers:', error);
  //     toast.error('Failed to load AI providers');
  //     const fallbackProviders = [
  //       { id: 1, provider: 'OpenAI' },
  //       { id: 2, provider: 'Anthropic' },
  //       { id: 3, provider: 'Mistral' },
  //       { id: 4, provider: 'Cohere' }
  //     ];
  //     setAiProviders(fallbackProviders);
  //     setSelectedApiProvider('');
  //   } finally {
  //     setProvidersLoading(false);
  //   }
  // };

  const fetchAiModels = async (providerId) => {
    try {
      setModelsLoading(true);
      const response = await axiosInstance.get(`/ai_tools/ai_model_dropdown?provider_id=${providerId}`);

      let models = [];
      if (response.data && Array.isArray(response.data)) {
        models = response.data;
      } else {
        models = response.data?.data || [];
      }

      setAiModels(models);

      setAiTools(prev => prev.map(tool => ({
        ...tool,
        models: models.map(model => model.model),
        selectedModel: ''
      })));

      return models;
    } catch (error) {
      console.error('Error fetching AI models:', error);
      toast.error('Failed to load AI models');
      setAiModels([]);

      setAiTools(prev => prev.map(tool => ({
        ...tool,
        models: [],
        selectedModel: ''
      })));
      return [];
    } finally {
      setModelsLoading(false);
    }
  };

  const fetchExistingAiTool = async (projectId) => {
    try {
      if (!projectId) return;

      const response = await axiosInstance.get(`/ai_tools/get_ai_tool?project_id=${projectId}`);

      if (response.status_code === 200 && response.data) {
        toast.success(response.message)
        setExistingAiTool(response.data);

        const provider = aiProviders?.find(p => p.id === response.data.provider_id);
        if (provider) {
          setSelectedApiProvider(provider.provider);

          const models = await fetchAiModels(response.data.provider_id);

          const model = models.find(m => m.id === response.data.model_id);
          if (model) {
            setAiTools(prev => prev.map(tool => ({
              ...tool,
              selectedModel: model.model
            })));
          }
        }
      } else {
        setExistingAiTool(null);
        setSelectedApiProvider('');
        setAiTools(prev => prev.map(tool => ({
          ...tool,
          selectedModel: ''
        })));
      }
    } catch (error) {
      toast.error('Error fetching existing AI tool:', error);
      setExistingAiTool(null);
      setSelectedApiProvider('');
      setAiTools(prev => prev.map(tool => ({
        ...tool,
        selectedModel: ''
      })));
    }
  };

  const handleSaveAiTool = async (tool) => {
    if (!selectedProject) {
      toast.error('Please select a project');
      return;
    }

    if (!apiKeyInput.trim()) {
      toast.error('Please enter API Key');
      return;
    }

    if (!selectedApiProvider) {
      toast.error('Please select an API provider');
      return;
    }

    const selectedProviderObj = aiProviders.find(provider => provider.provider === selectedApiProvider);
    if (!selectedProviderObj) {
      toast.error('Please select a valid API provider');
      return;
    }

    if (!tool.selectedModel) {
      toast.error('Please select a model');
      return;
    }

    const selectedModelObj = aiModels.find(model => model.model === tool.selectedModel);
    if (!selectedModelObj) {
      toast.error('Please select a valid model');
      return;
    }

    try {
      setSaving(true);

      const requestData = {
        project_id: parseInt(selectedProject),
        provider_id: selectedProviderObj.id,
        model_id: selectedModelObj.id,
        api_key: apiKeyInput.trim()
      };

      const response = await axiosInstance.post('/ai_tools/add_ai_tool', requestData);
      toast.success(response?.message || 'AI tool configuration saved successfully');

      setAiTools(prev => prev.map(t =>
        t.id === tool.id
          ? {
            ...t,
            status: 'connected',
            apiKey: '•'.repeat(12)
          }
          : t
      ));

      setExistingAiTool({
        project_id: parseInt(selectedProject),
        provider_id: selectedProviderObj.id,
        model_id: selectedModelObj.id,
        provider: selectedApiProvider,
        model: tool.selectedModel
      });

    } catch (error) {
      console.error('Error saving AI tool configuration:', error);
      const errorMessage = error?.response?.data?.detail?.message ||
        error?.response?.data?.message ||
        'Failed to save AI tool configuration';
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };


  // const fetchProjects = async () => {
  //   try {
  //     setProjectsLoading(true);
  //     const response = await axiosInstance.get('/projects/list_project?page=1&page_size=100&sort_type=2&sort_order=2');
  //     setProjects(response.projects || []);
  //     setSelectedProject('');
  //   } catch (error) {
  //     console.error('Error fetching projects:', error);
  //     toast.error('Failed to load projects');
  //   } finally {
  //     setProjectsLoading(false);
  //   }
  // };

  useEffect(() => {
    fetchProjectConfigs(currentPage, debouncedSearchTerm);
    fetchAiTools();
  }, [currentPage, debouncedSearchTerm, sortConfig, fetchProjectConfigs]);

  useEffect(() => {
    if (selectedProject && aiProviders.length > 0) {
      fetchExistingAiTool(selectedProject);
    } else {
      setExistingAiTool(null);
      setSelectedApiProvider('');
      setAiTools(prev => prev.map(tool => ({
        ...tool,
        selectedModel: ''
      })));
    }
  }, [selectedProject, aiProviders]);

  useEffect(() => {
    if (!healdedProjectData?.projects) return;

    const healedIds = healdedProjectData.projects
      .filter((p) => p.self_healing)
      .map((p) => p.project_id);

    setEnabledProjectIds(healedIds);
  }, [healdedProjectData]);


  const fetchAiTools = async () => {
    try {
      setToolsLoading(true);
      await new Promise(resolve => setTimeout(resolve, 1000));
      setAiTools(dummyAiTools);
    } catch (error) {
      console.error('Error fetching AI tools:', error);
      toast.error('Failed to load AI tools');
      setAiTools(dummyAiTools);
    } finally {
      setToolsLoading(false);
    }
  };

  const handleApiProviderChange = (providerName) => {
    setSelectedApiProvider(providerName);

    const selectedProvider = aiProviders.find(provider => provider.provider === providerName);
    if (selectedProvider) {
      fetchAiModels(selectedProvider.id);
    }

    setAiTools(prev => prev.map(tool => ({
      ...tool,
      selectedModel: ''
    })));
  };

  const handleModelChange = async (toolId, model) => {
    setAiTools(prev =>
      prev.map(tool =>
        tool.id === toolId ? { ...tool, selectedModel: model } : tool
      )
    );

    // Only proceed if a project is selected
    if (!selectedProject) {
      toast.error('Please select a project before changing the model');
      return;
    }

    // Find provider & model IDs
    const selectedProviderObj = aiProviders.find(provider => provider.provider === selectedApiProvider);
    const selectedModelObj = aiModels.find(m => m.model === model);

    if (!selectedProviderObj || !selectedModelObj) {
      toast.error('Invalid provider or model selected');
      return;
    }

    try {
      setSaving(true);
      const requestData = {
        project_id: parseInt(selectedProject),
        model_id: selectedModelObj.id
      };

      const response = await axiosInstance.put('/ai_tools/update_ai_tool', requestData);

      if (response?.status_code === 200) {
        toast.success(response.message || 'AI Tool updated successfully');
      } else {
        toast.error('Failed to update AI tool');
      }
    } catch (error) {
      console.error('Error updating AI tool:', error);
      const errorMessage =
        error?.response?.data?.detail?.message ||
        error?.response?.data?.message ||
        'Failed to update AI tool';
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };


  const handleAddConfiguration = () => {
    setEditingConfig(null);
    setIsEditMode(false);
    setIsModalOpen(true);
  };

  const handleEditConfiguration = async (config) => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`/projects/config?project_id=${config.project_id}`);
      setEditingConfig(response.config);
      setIsEditMode(true);
      setIsModalOpen(true);
    } catch (error) {
      console.error('Error fetching configuration details:', error);
      toast.error('Failed to fetch configuration details');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveConfiguration = async (configData) => {
    try {
      if (isEditMode) {
        await axiosInstance.put('/projects/config', configData);
        // toast.success('Project configuration updated successfully');
      } else {
        await axiosInstance.post('/projects/config', configData);
        // toast.success('Project configuration created successfully');
      }

      setIsModalOpen(false);
      setEditingConfig(null);
      setIsEditMode(false);
      fetchProjectConfigs();
    } catch (error) {
      console.error('Error saving configuration:', error);
      toast.error(isEditMode ? 'Failed to update configuration' : 'Failed to create configuration');
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingConfig(null);
    setIsEditMode(false);
  };

//     const selectedProjects = useMemo(() => {
//   return projects?.filter(project =>
//     enabledProjectIds.includes(project.project_id)
//   );
// }, [projects, enabledProjectIds]);





const handleSelfHealToggle = async (project) => {
  const previousIds = [...enabledProjectIds];
  const isEnabled = previousIds.includes(project.project_id);

  const updatedIds = isEnabled
    ? previousIds.filter(id => id !== project.project_id)
    : [...previousIds, project.project_id];

  setEnabledProjectIds(updatedIds);

  const selfHealPayload ={
    project_id:project.project_id,
    self_healing:!isEnabled
  }

  try {
    await selfHealTriggerFn(selfHealPayload);
  } catch (error) {
    setEnabledProjectIds(previousIds);
  }
};



  return (
    <>
      <div className="settings-section">
        <div className="settings-section-intro">
          <h2 className="settings-section-title">Configuration</h2>
          <p className="settings-subtitle">Project-level automation and environment defaults.</p>
        </div>

        <div className="p-6 space-y-10 min-w-0 max-w-full">
          {/* Search and Add controls */}
          {(configs.length > 0 || showAddButton) && (
            <div className="flex flex-wrap justify-between items-center gap-4 min-w-0">
              {configs.length > 0 ? (
                <div className="flex-1 relative min-w-0">
                  <input
                    type="text"
                    placeholder="Search by project name..."
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setCurrentPage(1); // Reset to first page on search
                    }}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <span className="absolute right-3 top-2.5 text-gray-400">
                    <i className="fas fa-search"></i>
                  </span>
                </div>
              ) : (
                <div className="flex-1" />
              )}
              {/* {showAddButton && (
                <button
                  onClick={handleAddConfiguration}
                  className="btn btn-primary flex-shrink-0"
                >
                  <i className="fas fa-plus"></i>
                  Add Project Configuration
                </button>
              )} */}
            </div>
          )}

          {loading && configs.length === 0 ? (
            <div className="flex justify-center items-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : error && error !== "No project configs found" ? (
            <div className="text-center py-6">
              <p className="text-gray-500 text-sm mb-4">{error}</p>
            </div>
          ) : configs.length > 0 ? (
            <div className="bg-gray-100 bg-gradient-to-r to-indigo-50 rounded-xl min-w-0 max-w-full">
              <div className="overflow-x-auto overflow-y-auto max-h-[600px] min-w-0">
                <table className="w-full min-w-[640px] table-fixed rounded-lg">
                  <thead className="bg-gradient-to-r from-gray-50 to-gray-100 sticky top-0 z-10">
                    <tr>
                      <th className="w-[40%] px-4 sm:px-6 py-4 text-left text-sm font-semibold text-gray-700 uppercase tracking-wider">
                        Project Name
                      </th>
                      <th className="w-[20%] px-4 sm:px-6 py-4 text-center text-sm font-semibold text-gray-700 uppercase tracking-wider">
                        Max Scenarios
                      </th>
                      <th className="w-[20%] px-4 sm:px-6 py-4 text-center text-sm font-semibold text-gray-700 uppercase tracking-wider">
                        Max Test Cases
                      </th>
                      <th className="w-[20%] px-4 sm:px-6 py-4 text-center text-sm font-semibold text-gray-700 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {configs.map((config, index) => (
                      <tr
                        key={config.id}
                        className={`${index % 2 === 0 ? "bg-white" : "bg-gray-50"} hover:bg-blue-50 transition-colors`}
                      >
                        <td className="px-4 sm:px-6 py-4 max-w-0">
                          <div className="flex items-center min-w-0">
                            <div className="flex-shrink-0 h-10 w-10">
                              <div className="h-10 w-10 rounded-full bg-gradient-to-r from-blue-400 to-indigo-400 flex items-center justify-center">
                                <i className="fas fa-project-diagram text-white text-sm"></i>
                              </div>
                            </div>
                            <div className="ml-4 min-w-0 flex-1">
                              <div
                                className="text-sm font-medium text-gray-900 truncate"
                                title={config.project_name}
                              >
                                {config.project_name}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-center">
                          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                            {config.max_scenarios}
                          </span>
                        </td>
                        <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-center">
                          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                            {config.max_test_cases}
                          </span>
                        </td>
                        <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-center">
                          <button
                            onClick={() => handleEditConfiguration(config)}
                            disabled={loading}
                            className="btn btn-primary"
                          >
                            {loading ? (
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                            ) : (
                              <i className="fas fa-edit"></i>
                            )}
                            Edit
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination Controls — outside scroll so always visible */}
              {totalPages > 1 && (
                <div className="mt-4 px-6 pb-4">
                  <Pagination
                    page={currentPage}
                    pageSize={pageSize}
                    totalPages={totalPages}
                    total={Math.max(configs.length, (totalPages - 1) * pageSize + configs.length)}
                    onPageChange={(page) => setCurrentPage(page)}
                    onPageSizeChange={(size) => {
                      setPageSize(size);
                      setCurrentPage(1);
                    }}
                    pageSizeOptions={[5, 10, 20, 50]}
                  />
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="text-gray-400 text-6xl mb-4">
                <i className="fas fa-cog"></i>
              </div>
              <h3 className="text-xl font-medium text-gray-700 mb-2">
                No Configurations Found
              </h3>
              <p className="text-gray-500 mb-6">
                There are no project configurations set up yet. Create your
                first configuration to get started.
              </p>
              {(showAddButton || error === "No project configs found") && (
                <button
                  onClick={handleAddConfiguration}
                  className="bg-gradient-to-r from-blue-600 to-indigo-700 hover:from-blue-700 hover:to-indigo-800 text-white px-6 py-3 rounded-lg font-medium transition-all transform hover:scale-105"
                >
                  Create First Configuration
                </button>
              )}
            </div>
          )}

          <div
            className="bg-gradient-to-r to-indigo-50 rounded-xl shadow-inner p-6 min-w-0 max-w-full overflow-hidden"
            style={{ backgroundColor: "#f8f9fa" }}
          >
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-800 mb-2">
                  Configuration Profile
                </h2>
                <p className="text-gray-600">
                  Save different API configurations to quickly switch between
                  providers and settings.
                  {existingAiTool && (
                    <span className="text-green-600 font-medium ml-2">
                      • Existing configuration loaded for this project
                    </span>
                  )}
                </p>
              </div>
            </div>

            {toolsLoading || providersLoading ? (
              <div className="flex justify-center items-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : (
              <div className="space-y-6">
                {aiTools.map((tool) => (
                  <div
                    key={tool.id}
                    className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
                  >
                    <div className="mb-6">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Select a Project
                      </label>
                      <ProjectSelectDropdown
                        value={selectedProject}
                        onChange={setSelectedProject}
                        placeholder="Select"
                      />
                    </div>

                    <div className="flex items-center justify-between mb-6">
                      <div className="flex items-center gap-4 w-full">
                        <div className="w-full">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            API Provider
                          </label>
                          <Select
                            value={selectedApiProvider}
                            onChange={(e) =>
                              handleApiProviderChange(e.target.value)
                            }
                            className="rt-select--form"
                            disabled={providersLoading || !selectedProject}
                          >
                            <option value="">Select</option>
                            {providersLoading ? (
                              <option>Loading providers...</option>
                            ) : (
                              aiProviders.map((provider) => (
                                <option
                                  key={provider.id}
                                  value={provider.provider}
                                >
                                  {provider.provider}
                                </option>
                              ))
                            )}
                          </Select>
                        </div>
                      </div>
                    </div>

                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Model
                      </label>
                      <Select
                        value={tool.selectedModel}
                        onChange={(e) =>
                          handleModelChange(tool.id, e.target.value)
                        }
                        className="rt-select--form"
                        disabled={
                          modelsLoading ||
                          !selectedApiProvider ||
                          !selectedProject
                        }
                      >
                        <option value="">Select</option>
                        {modelsLoading ? (
                          <option>Loading models...</option>
                        ) : tool.models.length === 0 ? (
                          <option value="">No models available</option>
                        ) : (
                          tool.models.map((model) => (
                            <option key={model} value={model}>
                              {model}
                            </option>
                          ))
                        )}
                      </Select>
                      {modelsLoading && (
                        <p className="text-sm text-gray-500 mt-1">
                          Loading models...
                        </p>
                      )}
                      {!selectedApiProvider && (
                        <p className="text-sm text-gray-500 mt-1">
                          Please select an API provider first
                        </p>
                      )}
                    </div>

                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {selectedApiProvider || "API Provider"} API Key
                        </label>
                        <div className="flex items-center gap-3">
                          <input
                            type="password"
                            value={apiKeyInput}
                            onChange={(e) => setApiKeyInput(e.target.value)}
                            placeholder={`Enter your ${selectedApiProvider || "API Provider"} API Key`}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            disabled={!selectedProject || !selectedApiProvider}
                          />
                        </div>
                      </div>

                      <button
                        onClick={() => handleSaveAiTool(tool)}
                        disabled={
                          saving ||
                          !selectedProject ||
                          !selectedApiProvider ||
                          !tool.selectedModel
                        }
                        className="btn btn-primary"
                      >
                        {saving ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 "></div>
                            Saving...
                          </>
                        ) : (
                          "Save Configuration"
                        )}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
       
       {/* Self heal component */}
          <SelfHeal
            projects={projects}
            selectedProjectIds={enabledProjectIds}
            onToggle={handleSelfHealToggle}
          />
        </div>
      </div>

      {isModalOpen && (
        <ProjectConfigModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          onSave={handleSaveConfiguration}
          editingConfig={editingConfig}
          isEditMode={isEditMode}
        />
      )}
    </>
  );
};

export default ProjectConfiguration;